import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Badge } from "reactstrap";

import avatar1 from "../../assets/images/users/avatar-1.jpg";
import StudioSwitcherModal from "../Studio/StudioSwitcherModal";
import {
    MEMBERSHIPS_CHANGED_EVENT,
    OPEN_STUDIO_SWITCHER_EVENT,
} from "../Studio/MembershipInviteWatcher";
import { AuthUserPayload, getStoredAuth } from "../../helpers/auth_storage";
import {
    isArrivedChimeMuted,
    setArrivedChimeMuted,
} from "../../helpers/arrivedFeedback";
import {
    isSupportBeepMuted,
    setSupportBeepMuted,
} from "../../helpers/supportTicketUx";
import { getMemberships, logout } from "../../services/authService";
import { logoutUserSuccess } from "../../slices/auth/login/reducer";

const ROLE_I18N_KEYS: Record<string, string> = {
    owner: "roles.owner",
    manager: "roles.manager",
    receptionist: "roles.receptionist",
    professional: "roles.professional",
};

const selectLoginSession = createSelector(
    (state: { Login: { user: AuthUserPayload | Record<string, never> } }) =>
        state.Login.user,
    (user) => user
);

const resolveAuthPayload = (
    loginSession: AuthUserPayload | Record<string, unknown>
): AuthUserPayload | null => {
    if (
        loginSession &&
        typeof loginSession === "object" &&
        "token" in loginSession &&
        typeof loginSession.token === "string" &&
        loginSession.token !== ""
    ) {
        return loginSession as AuthUserPayload;
    }

    return getStoredAuth();
};

const resolveDisplayName = (
    authPayload: AuthUserPayload | null,
    fallback: string
): string => {
    const user = authPayload?.user;

    if (typeof user?.name === "string" && user.name.trim() !== "") {
        return user.name.trim();
    }

    if (typeof user?.email === "string" && user.email.trim() !== "") {
        return user.email.trim();
    }

    return fallback;
};

const resolvePrimaryRole = (authPayload: AuthUserPayload | null): string => {
    const roles = authPayload?.user?.roles;

    if (!Array.isArray(roles) || roles.length === 0) {
        return "";
    }

    return String(roles[0]);
};

const resolveRoleLabel = (role: string, t: TFunction): string => {
    if (!role) {
        return "";
    }

    const roleKey = ROLE_I18N_KEYS[role];

    return roleKey ? t(roleKey) : role;
};

const ProfileDropdown = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const loginSession = useSelector(selectLoginSession);
    const [isProfileDropdown, setIsProfileDropdown] = useState(false);
    const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
    const [highlightMembershipId, setHighlightMembershipId] = useState<number | null>(
        null
    );
    const [canSwitchStudio, setCanSwitchStudio] = useState(false);
    const [pendingInviteCount, setPendingInviteCount] = useState(0);
    const [tenantName, setTenantName] = useState<string>("");
    const [beepMuted, setBeepMuted] = useState(() => isSupportBeepMuted());
    const [arrivedChimeMuted, setArrivedChimeMutedState] = useState(() =>
        isArrivedChimeMuted()
    );

    const authPayload = useMemo(
        () => resolveAuthPayload(loginSession),
        [loginSession]
    );

    const defaultName = t("header.profile.defaultName");
    const userName = useMemo(
        () => resolveDisplayName(authPayload, defaultName),
        [authPayload, defaultName]
    );
    const roleLabel = useMemo(() => {
        const primaryRole = resolvePrimaryRole(authPayload);

        return resolveRoleLabel(primaryRole, t);
    }, [authPayload, t]);

    const subtitle = useMemo(() => {
        if (tenantName && roleLabel) {
            return t("header.profile.subtitle", {
                tenant: tenantName,
                role: roleLabel,
            });
        }

        return roleLabel;
    }, [roleLabel, t, tenantName]);

    useEffect(() => {
        let cancelled = false;

        const loadMembershipContext = async () => {
            try {
                const response = await getMemberships();

                if (cancelled || !response.success || !response.data) {
                    return;
                }

                const memberships = response.data.memberships || [];
                const pendingCount = memberships.filter(
                    (item) => item.status === "pending"
                ).length;

                setPendingInviteCount(pendingCount);
                setCanSwitchStudio(
                    memberships.filter(
                        (item) => item.status === "active" || item.status === "pending"
                    ).length > 1 || pendingCount > 0
                );

                const current = memberships.find(
                    (item) => item.tenant_id === response.data?.current_tenant_id
                );

                setTenantName(current?.tenant_name || "");
            } catch {
                // Header subtitle remains role-only when memberships fail.
            }
        };

        void loadMembershipContext();

        const onMembershipsChanged = () => {
            void loadMembershipContext();
        };

        window.addEventListener(MEMBERSHIPS_CHANGED_EVENT, onMembershipsChanged);

        return () => {
            cancelled = true;
            window.removeEventListener(MEMBERSHIPS_CHANGED_EVENT, onMembershipsChanged);
        };
    }, [authPayload?.token]);

    useEffect(() => {
        const openSwitcher = (event: Event) => {
            const custom = event as CustomEvent<{ membershipId?: number | null }>;
            const membershipId = custom.detail?.membershipId;
            setHighlightMembershipId(
                typeof membershipId === "number" && Number.isFinite(membershipId)
                    ? membershipId
                    : null
            );
            setIsProfileDropdown(false);
            setIsSwitcherOpen(true);
        };

        window.addEventListener(OPEN_STUDIO_SWITCHER_EVENT, openSwitcher);

        return () => {
            window.removeEventListener(OPEN_STUDIO_SWITCHER_EVENT, openSwitcher);
        };
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.get("studio_invite") === "1") {
            const rawMembershipId = params.get("membership_id");
            const membershipId = rawMembershipId ? Number(rawMembershipId) : NaN;
            setHighlightMembershipId(
                Number.isFinite(membershipId) ? membershipId : null
            );
            setIsSwitcherOpen(true);
            params.delete("studio_invite");
            params.delete("membership_id");
            const next = params.toString();
            const path = `${window.location.pathname}${next ? `?${next}` : ""}`;
            window.history.replaceState({}, "", path);
        }
    }, []);

    const toggleProfileDropdown = () => {
        setIsProfileDropdown((open) => !open);
    };

    const handleLogout = async () => {
        await logout();
        dispatch(logoutUserSuccess(true));
        navigate("/login");
    };

    return (
        <Dropdown
            isOpen={isProfileDropdown}
            toggle={toggleProfileDropdown}
            className="ms-3 ms-sm-4 header-item topbar-user"
        >
            <DropdownToggle tag="button" type="button" className="btn">
                <span className="d-flex align-items-center">
                    <span className="position-relative d-inline-block">
                        <img
                            className="rounded-circle header-profile-user"
                            src={avatar1}
                            alt={t("header.profile.avatarAlt")}
                        />
                        {pendingInviteCount > 0 ? (
                            <Badge
                                color="warning"
                                pill
                                className="position-absolute top-0 start-100 translate-middle"
                                style={{ fontSize: "0.65rem" }}
                                aria-label={t("auth.switch_studio.pending_badge_aria", {
                                    count: pendingInviteCount,
                                })}
                            >
                                {pendingInviteCount}
                            </Badge>
                        ) : null}
                    </span>
                    <span className="text-start ms-xl-2">
                        <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
                            {userName}
                        </span>
                        {subtitle ? (
                            <span className="d-none d-xl-block ms-1 fs-12 text-muted user-name-sub-text">
                                {subtitle}
                            </span>
                        ) : null}
                    </span>
                </span>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
                <h6 className="dropdown-header">
                    {t("header.profile.welcome", { name: userName })}
                </h6>
                <DropdownItem tag={Link} to="/settings/account" className="p-0">
                    <span className="dropdown-item">
                        <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1" />
                        <span className="align-middle">{t("header.profile.profile")}</span>
                    </span>
                </DropdownItem>
                <DropdownItem tag={Link} to="/settings/subscription" className="p-0">
                    <span className="dropdown-item">
                        <i className="mdi mdi-credit-card-outline text-muted fs-16 align-middle me-1" />
                        <span className="align-middle">
                            {t("header.profile.subscription")}
                        </span>
                    </span>
                </DropdownItem>
                <DropdownItem tag={Link} to="/help" className="p-0">
                    <span className="dropdown-item">
                        <i className="mdi mdi-help-circle-outline text-muted fs-16 align-middle me-1" />
                        <span className="align-middle">{t("header.profile.help")}</span>
                    </span>
                </DropdownItem>
                <DropdownItem tag={Link} to="/support/tickets" className="p-0">
                    <span className="dropdown-item">
                        <i className="mdi mdi-headset text-muted fs-16 align-middle me-1" />
                        <span className="align-middle">{t("header.profile.myTickets")}</span>
                    </span>
                </DropdownItem>
                <DropdownItem
                    className="p-0"
                    onClick={() => {
                        const next = !beepMuted;
                        setSupportBeepMuted(next);
                        setBeepMuted(next);
                    }}
                >
                    <span className="dropdown-item">
                        <i
                            className={`mdi ${
                                beepMuted ? "mdi-volume-high" : "mdi-volume-off"
                            } text-muted fs-16 align-middle me-1`}
                        />
                        <span className="align-middle">
                            {beepMuted
                                ? t("header.profile.unmuteSupportBeep")
                                : t("header.profile.muteSupportBeep")}
                        </span>
                    </span>
                </DropdownItem>
                <DropdownItem
                    className="p-0"
                    onClick={() => {
                        const next = !arrivedChimeMuted;
                        setArrivedChimeMuted(next);
                        setArrivedChimeMutedState(next);
                    }}
                >
                    <span className="dropdown-item">
                        <i
                            className={`mdi ${
                                arrivedChimeMuted
                                    ? "mdi-bell-ring-outline"
                                    : "mdi-bell-off-outline"
                            } text-muted fs-16 align-middle me-1`}
                        />
                        <span className="align-middle">
                            {arrivedChimeMuted
                                ? t("header.profile.unmuteArrivedChime")
                                : t("header.profile.muteArrivedChime")}
                        </span>
                    </span>
                </DropdownItem>
                {canSwitchStudio ? (
                    <>
                        <div className="dropdown-divider" />
                        <DropdownItem
                            className="p-0"
                            onClick={() => {
                                setIsProfileDropdown(false);
                                setIsSwitcherOpen(true);
                            }}
                        >
                            <span className="dropdown-item text-primary d-flex align-items-center justify-content-between gap-2">
                                <span>
                                    <i className="mdi mdi-swap-horizontal text-primary fs-16 align-middle me-1" />
                                    <span className="align-middle">
                                        {t("auth.switch_studio.title")}
                                    </span>
                                </span>
                                {pendingInviteCount > 0 ? (
                                    <Badge
                                        color="warning"
                                        pill
                                        className="ms-auto"
                                        aria-label={t("auth.switch_studio.pending_badge_aria", {
                                            count: pendingInviteCount,
                                        })}
                                    >
                                        {pendingInviteCount}
                                    </Badge>
                                ) : null}
                            </span>
                        </DropdownItem>
                    </>
                ) : null}
                <div className="dropdown-divider" />
                <DropdownItem className="p-0" onClick={handleLogout}>
                    <span className="dropdown-item text-danger">
                        <i className="mdi mdi-logout text-danger fs-16 align-middle me-1" />
                        <span className="align-middle">{t("header.profile.logout")}</span>
                    </span>
                </DropdownItem>
            </DropdownMenu>
            <StudioSwitcherModal
                isOpen={isSwitcherOpen}
                toggle={() => {
                    setIsSwitcherOpen((open) => {
                        if (open) {
                            setHighlightMembershipId(null);
                        }

                        return !open;
                    });
                }}
                highlightMembershipId={highlightMembershipId}
            />
        </Dropdown>
    );
};

export default ProfileDropdown;
